import { useRef, useState, useCallback } from 'react';
import { useSpeechSynthesis } from './useSpeechSynthesis';

interface DriverCallData {
  id: number;
  queue: {
    driver: {
      name: string;
    };
    truck: {
      licensePlate: string;
    };
  };
}

interface UseDriverCallOptions {
  callTimes?: number;
  audioDelay?: number;
  speechDelay?: number;
  audioPath?: string;
}

interface UseDriverCallCallbacks {
  onCallStart?: () => void;
  onBeforeCall?: () => void;
  onCallEnd?: () => void;
}

interface UseDriverCallReturn {
  isCallingDriver: boolean;
  callDriver: (driverData: DriverCallData, callbacks?: UseDriverCallCallbacks) => Promise<void>;
}

const DEFAULT_CALL_TIMES = 3;
const DEFAULT_AUDIO_DELAY = 2000;
const DEFAULT_SPEECH_DELAY = 6000;

export function useDriverCall(options: UseDriverCallOptions = {}): UseDriverCallReturn {
  const { callTimes = DEFAULT_CALL_TIMES, audioDelay = DEFAULT_AUDIO_DELAY, speechDelay = DEFAULT_SPEECH_DELAY } = options;

  const { talk } = useSpeechSynthesis();

  const [isCallingDriver, setIsCallingDriver] = useState(false);
  const isCallingRef = useRef(false);
  const lastCalledIdRef = useRef<number | null>(null);
  const isFirstCallRef = useRef(true);

  // Função auxiliar para pausar a execução
  const delay = useCallback((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)), []);

  const callDriver = useCallback(
    async (driverData: DriverCallData, callbacks?: UseDriverCallCallbacks) => {
      if (!driverData) {
        console.log('❌ useDriverCall - Sem dados do motorista, não executando');
        return;
      }

      if (isCallingRef.current) {
        console.log('⏳ useDriverCall - Já está chamando, ignorando:', driverData.queue?.driver?.name);
        return;
      }

      // Se é o mesmo driver que já foi chamado, não chama novamente
      // if (lastCalledIdRef.current === driverData.id) {
      //   console.log('🔄 useDriverCall - Mesmo driver já foi chamado:', driverData.queue?.driver?.name);
      //   return;
      // }

      console.log('🔊 useDriverCall - Iniciando chamada de voz para:', driverData.queue?.driver?.name);

      // Executa callback antes do início da chamada
      callbacks?.onBeforeCall?.();

      // Marca que está chamando e salva o ID
      isCallingRef.current = true;
      lastCalledIdRef.current = driverData.id;
      setIsCallingDriver(true);

      // Executa callback no início da chamada
      callbacks?.onCallStart?.();

      console.log('🎤 useDriverCall - callDriver executado para:', driverData.queue.driver.name);
      const { driver, truck } = driverData.queue;

      // Se é a primeira chamada, adiciona +1 ao callTimes
      const actualCallTimes = isFirstCallRef.current ? callTimes + 1 : callTimes;
      isFirstCallRef.current = false;

      try {
        for (let i = 0; i < actualCallTimes; i++) {
          console.log(`🔄 useDriverCall - Chamada ${i + 1}/${actualCallTimes} para:`, driver.name);

          // Aguarda tempo definido após o áudio
          await delay(audioDelay);

          const phrases = [`Chamando motorista ${driver.name}`, `com a placa ${truck.licensePlate}`];

          for (let phraseIndex = 0; phraseIndex < phrases.length; phraseIndex++) {
            const phrase = phrases[phraseIndex];
            const isLastCall = i === actualCallTimes - 1;
            const isLastPhrase = phraseIndex === phrases.length - 1;

            console.log('🗣️ useDriverCall - Falando:', phrase);

            // Se é a última chamada E a última frase, passa o callback
            if (isLastCall && isLastPhrase) {
              console.log('%c 🏁 useDriverCall - Última frase da última chamada, configurando onCallEnd', 'font-weight: bold; color: red;');
              talk(phrase, () => {
                console.log('%c ✅ useDriverCall - Callback onCallEnd executado após última frase', 'font-weight: bold; color: green;');
                callbacks?.onCallEnd?.();
              });
            } else {
              talk(phrase);
            }
          }

          // Aguarda tempo definido antes de chamar novamente (exceto na última iteração)
          if (i < actualCallTimes - 1) {
            await delay(speechDelay);
          }
        }

        console.log('✅ useDriverCall - Finalizou todas as chamadas para:', driver.name);
      } finally {
        // Sempre executa, mesmo em caso de erro
        isCallingRef.current = false;
        setIsCallingDriver(false);
      }
    },
    [talk, callTimes, audioDelay, speechDelay, delay]
  );

  return {
    isCallingDriver,
    callDriver,
  };
}
